import { useTranslation } from 'react-i18next';
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, Lock, FileText,
  Truck, UserCog, Building2, MapPin, Calendar, ArrowDownRight,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { useTrip, useConfirmTrip, useCancelTrip, useDisputeTrip } from '@/hooks/useTrips';
import { PhotoUploadField } from '@/components/trips/PhotoUploadField';
import { PhotoLightbox }    from '@/components/trips/PhotoLightbox';
import { useTripPhotos }    from '@/hooks/useTripPhotos';
import { useState as __pr9aUseState } from 'react';
import { TripStatusBadge } from '@/components/trips/TripStatusBadge';

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB'); } catch { return String(d); }
}
import { LiveMathPreview } from '@/components/contracts/LiveMathPreview';
import { formatSar } from '@/lib/trip-math';
import type { Trip } from '@/lib/types';
import { TripOcrSection } from '@/components/ocr/TripOcrSection';
import { TripConsumptionSection } from '@/components/trips/TripConsumptionSection';

function TripEdit({ trip }: { trip: { id: string; tripDate?: string; deliveredQty?: unknown;
  receiptNumber?: string | null; notes?: string | null } }) {
  const qc = useQueryClient();
  const [open, setOpen] = __pr9aUseState(false);
  const [date, setDate] = __pr9aUseState(String(trip.tripDate ?? '').slice(0, 10));
  const [qty, setQty] = __pr9aUseState(String(trip.deliveredQty ?? ''));
  const [receipt, setReceipt] = __pr9aUseState(String(trip.receiptNumber ?? ''));
  const [note, setNote] = __pr9aUseState(String(trip.notes ?? ''));
  const [busy, setBusy] = __pr9aUseState(false);
  const [err, setErr] = __pr9aUseState<string | null>(null);
  const [done, setDone] = __pr9aUseState(false);

  const save = async () => {
    setBusy(true); setErr(null); setDone(false);
    try {
      await apiClient.patch(`/api/v1/trips/${trip.id}`, {
        ...(date && { tripDate: date }),
        ...(qty !== '' && { deliveredQty: Number(qty) }),
        receiptNumber: receipt.trim() || null,
        notes: note.trim() || null,
      });
      await qc.invalidateQueries({ queryKey: ['trips'] });
      await qc.invalidateQueries({ queryKey: ['trip', trip.id] });
      setDone(true);
    } catch (e) { setErr(extractErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="pt-2 border-t border-border">
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>Edit this trip</Button>
      </div>
    );
  }

  return (
    <div className="pt-3 border-t border-border space-y-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">Edit</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground" htmlFor="te-date">Trip date</label>
          <Input id="te-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground" htmlFor="te-qty">Delivered qty</label>
          <Input id="te-qty" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground" htmlFor="te-rcpt">Receipt number</label>
        <Input id="te-rcpt" value={receipt} onChange={(e) => setReceipt(e.target.value)} placeholder="optional" />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground" htmlFor="te-note">Note</label>
        <Input id="te-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" />
      </div>
      {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
      {done && <p className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</p>}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Close</Button>
        <Button size="sm" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Vehicle, driver and contract are not editable here — those change who is paid.
      </p>
    </div>
  );
}

interface TripDetailSheetProps {
  /** When provided, shown immediately. Otherwise we fetch by id. */
  initial: Trip | null;
  onClose: () => void;
}

function MoneyRow({ icon: Icon, label, value, highlight }: { icon: React.ElementType; label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${highlight ? 'font-medium' : ''}`}>
      <span className="inline-flex items-center gap-2 text-sm">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className={highlight ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
      </span>
      <span className={`text-sm tabular-nums ${highlight ? 'text-foreground' : 'text-muted-foreground'}`}>{value}</span>
    </div>
  );
}

export function TripDetailSheet({ initial, onClose }: TripDetailSheetProps) {
  const { t } = useTranslation();
  const id = initial?.id;
  const { data: fresh, isLoading } = useTrip(id);
  const trip = fresh ?? initial;

  const confirmMutation = useConfirmTrip();

  const [lightboxOpen, setLightboxOpen]   = __pr9aUseState(false);
  const [lightboxIndex, setLightboxIndex] = __pr9aUseState(0);
  const { data: __pr9aPhotos = [] } = useTripPhotos(trip?.id);
  const cancelMutation  = useCancelTrip();
  const disputeMutation = useDisputeTrip();

  if (!trip) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        {isLoading
          ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          : <span className="text-sm text-muted-foreground">{t('trips.notFound')}</span>
        }
      </div>
    );
  }

  const handleConfirm = async () => {
    try { await confirmMutation.mutateAsync(trip.id); } catch { /* error surfaces via mutation state */ }
  };
  const handleCancel = async () => {
    if (!confirm(t('trips.confirmCancel'))) return;
    try { await cancelMutation.mutateAsync(trip.id); } catch { /* */ }
  };
  const handleDispute = async () => {
    try { await disputeMutation.mutateAsync(trip.id); } catch { /* */ }
  };

  const canConfirm = trip.status === 'draft' && !trip.locked;
  const canCancel  = !['cancelled', 'paid'].includes(trip.status) && !trip.locked;
  const canDispute = ['confirmed', 'invoiced'].includes(trip.status) && !trip.locked;

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Status header */}
      <div className="flex items-center gap-2 flex-wrap">
        <TripStatusBadge status={trip.status} locked={trip.locked} />
        {trip.locked && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Lock className="h-3 w-3" /> {t('trips.lockedAt', { date: trip.lockedAt?.slice(0, 10) ?? '' })}
          </span>
        )}
      </div>

      {/* Quick facts */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('trips.field.date')}</div>
          <div className="inline-flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /> {formatDate(trip.tripDate)}</div>
        </div>
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('trips.field.tripType')}</div>
          <div>{t(`trips.tripType.${trip.tripType}`)}</div>
        </div>
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('trips.field.deliveredVsTarget')}</div>
          <div className="font-mono">
            {Number(trip.deliveredQty)} / {Number(trip.targetQty)} {trip.unit}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('trips.field.contract')}</div>
          <div className="font-mono text-xs">{trip.siteContract?.code ?? '—'}</div>
        </div>
      </div>

      {/* Parties */}
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t('trips.section.parties')}</div>
        <div className="rounded-lg border border-border bg-card p-3 space-y-2 text-sm">
          <div className="inline-flex items-center gap-2">
            <Building2 className="h-4 w-4 text-amber-500" />
            <span className="text-muted-foreground">{t('trips.field.client')}:</span>
            <span>{trip.client?.nameEn ?? '?'}</span>
          </div>
          <div className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4 text-cyan-500" />
            <span className="text-muted-foreground">{t('trips.field.site')}:</span>
            <span>{trip.site?.nameEn ?? '?'}</span>
          </div>
          <div className="inline-flex items-center gap-2">
            <Truck className="h-4 w-4 text-green-500" />
            <span className="text-muted-foreground">{t('trips.field.vehicle')}:</span>
            <span className="font-mono">{trip.vehicle?.plateEn ?? trip.vehicle?.code ?? '?'}</span>
          </div>
          <div className="inline-flex items-center gap-2">
            <UserCog className="h-4 w-4 text-purple-500" />
            <span className="text-muted-foreground">{t('trips.field.driver')}:</span>
            <span>{trip.driver?.fullNameEn ?? '?'}</span>
          </div>
        </div>
      </div>

      {/* Money breakdown */}
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('trips.section.money')}</div>
        <div className="rounded-lg border border-border bg-card p-4">
          <MoneyRow icon={Building2} label={t('trips.field.clientPayment')} value={formatSar(Number(trip.clientPayment))} highlight />
          <div className="my-2 h-px bg-border" />
          {/* PR-MAY16-COMPANY-PROFIT-NET-WATER — show net of water cost.
              Falls back to gross for old API responses that haven't selected
              the new column. */}
          <MoneyRow icon={ArrowDownRight} label={t('trips.field.companyKeep')} value={formatSar(Number(trip.companyKeepNetSar ?? trip.companyKeep))} />
          <MoneyRow icon={ArrowDownRight} label={t('trips.field.investorNet')} value={formatSar(Number(trip.investorNet))} />
          <MoneyRow icon={ArrowDownRight} label={t('trips.field.driverPay')} value={formatSar(Number(trip.driverPay))} />
        </div>
        {!trip.mathValid && (
          <div className="flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-700 text-xs">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">{t('trips.warnings.title')}</div>
              {(trip.mathWarnings ?? []).map((w, i) => (<div key={i}>{w}</div>))}
            </div>
          </div>
        )}
        {!trip.locked && (
          <p className="text-xs text-muted-foreground italic">
            {t('trips.provisionalNotice')}
          </p>
        )}
      </div>

      {/* Receipt */}
      {(trip.receiptNumber || trip.receiptPhotoUrl || trip.clientSignatory) && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('trips.section.receipt')}</div>
          <div className="rounded-lg border border-border bg-card p-3 text-sm space-y-1">
            {trip.receiptNumber && (
              <div className="inline-flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{t('trips.field.receiptNumber')}:</span>
                <span className="font-mono">{trip.receiptNumber}</span>
              </div>
            )}
            {trip.clientSignatory && (
              <div>
                <span className="text-muted-foreground me-1">{t('trips.field.clientSignatory')}:</span>
                <span>{trip.clientSignatory}</span>
              </div>
            )}
            {trip.receiptPhotoUrl && (
              <a href={trip.receiptPhotoUrl} target="_blank" rel="noreferrer"
                 className="text-xs text-blue-500 hover:underline inline-block mt-1">
                {t('trips.viewReceiptPhoto')}
              </a>
            )}
          </div>
        </div>
      )}

      {trip.notes && (
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('trips.field.notes')}</div>
          <div className="rounded-lg border border-border bg-card p-3 text-sm whitespace-pre-wrap">{trip.notes}</div>
        </div>
      )}

      {/* PR 9a: Receipt photos */}
      {trip && (
        <div className="space-y-2 pt-2 border-t border-border">
          <PhotoUploadField
            tripId={trip.id}
            tripCode={trip.code}
            onOpenLightbox={(photoId, photoIds) => {
              const idx = photoIds.indexOf(photoId);
              setLightboxIndex(idx >= 0 ? idx : 0);
              setLightboxOpen(true);
            }}
          />
        </div>
      )}
      <PhotoLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        photos={__pr9aPhotos}
        startIndex={lightboxIndex}
      />

      {/* PR W1: consumption section */}
      <TripConsumptionSection tripId={trip.id} />

      {/*
          Correcting a trip.
          
          Only the four fields that are actually wrong in practice: a date typed
          from the wrong ticket, a quantity read off the meter, a receipt number,
          a note. Vehicle, driver and contract are not here on purpose - changing
          those changes who gets paid and what the client owes, which belongs in
          a flow that asks why, not in a quick inline edit.
      */}
      <TripEdit trip={trip} />

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        {canConfirm && (
          <Button onClick={handleConfirm} disabled={confirmMutation.isPending}>
            {confirmMutation.isPending
              ? <><Loader2 className="h-4 w-4 me-1.5 animate-spin" /> {t('common.saving')}</>
              : <><CheckCircle2 className="h-4 w-4 me-1.5" /> {t('trips.action.confirm')}</>
            }
          </Button>
        )}
        {canDispute && (
          <Button variant="outline" onClick={handleDispute} disabled={disputeMutation.isPending}>
            <AlertTriangle className="h-4 w-4 me-1.5" /> {t('trips.action.dispute')}
          </Button>
        )}
        {canCancel && (
          <Button variant="ghost" onClick={handleCancel} disabled={cancelMutation.isPending}>
            <XCircle className="h-4 w-4 me-1.5" /> {t('trips.action.cancel')}
          </Button>
        )}
        <div className="flex-1" />
        <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
      </div>
            <TripOcrSection tripId={trip.id} />
        </div>
  );
}
